import { ADD_ON_SKUS, REGIONS } from '../constants';
import { QuoteData, QuoteProduct } from '../hooks/useGetQuote/types';
import { OrderItem, ProductData } from '../hooks/useGetProduct/types';
import isNZProduct from './IsNZProduct';

export const getRegionFromLegalEntity = (legalEntity?: string): string =>
  (legalEntity === 'MYOB Australia Pty Ltd' ? REGIONS.AUSTRALIA : REGIONS.NEW_ZEALAND);

const getDiscountAmount = (quoteProduct: QuoteProduct): number => {
  if (quoteProduct.discount !== undefined && quoteProduct.discount !== 0) {
    return Math.abs(quoteProduct.discount);
  }
  return Math.max(0, quoteProduct.listPrice - quoteProduct.totalPrice);
};

const mapQuoteProductToOrderItem = (
  quoteProduct: QuoteProduct,
  currency: string
): OrderItem => {
  const discount = quoteProduct.discounts?.[0];
  const discountAmount = getDiscountAmount(quoteProduct);
  const recurringInterval = quoteProduct.billingFrequency?.toUpperCase() === 'ANNUAL' ?
    'ANNUAL' : 'MONTHLY';

  return {
    lineNumber: Number.parseInt(quoteProduct.productId, 10),
    listPrice: quoteProduct.listPrice,
    listPriceWithoutTax: quoteProduct.listPrice - (quoteProduct.taxAmount || 0),
    quantity: 1,
    productId: quoteProduct.productId,
    productFullName: quoteProduct.productName ?? '',
    displayName: quoteProduct.productName ?? '',
    description: '',
    productFamily: quoteProduct.productFamily || quoteProduct.productName || '',
    productCode: quoteProduct.productId,
    productTier: '',
    totalPrice: quoteProduct.totalPrice,
    priceWithoutTax: quoteProduct.totalPrice - (quoteProduct.taxAmount || 0),
    tax: quoteProduct.taxAmount || 0,
    discountAmount,
    currency,
    deal: {
      id: discount?.dealId ? Number(discount.dealId) : 0,
      priceModificationValue: discount?.adjustmentValue ?? 0,
      priceModificationType: discount?.adjustmentType ?? '',
      freePeriod: discount?.freePeriod || 0,
      promotionName: discount?.promoName,
      availableTo: discount?.endDate,
      promoCode: discount?.promoCode,
      recurringInterval
    },
    _links: { downloads: [] }
  };
};

const mapQuoteProductToProductData = (
  quoteProduct: QuoteProduct,
  region: string,
  currency: string
): ProductData => {
  const discountAmount = getDiscountAmount(quoteProduct);
  const item = mapQuoteProductToOrderItem(quoteProduct, currency);

  return {
    region,
    totalPrice: quoteProduct.totalPrice,
    totalPriceWithoutTax: quoteProduct.totalPrice,
    totalDiscountAmt: discountAmount,
    tax: 0,
    currency,
    items: [item],
    isDiscountedProduct: discountAmount > 0
  };
};

export const mapQuoteToProductData = (
  quoteData: QuoteData,
  baseProductId: string,
  addonIds?: string[]
): ProductData => {
  const region = getRegionFromLegalEntity(quoteData.legalEntity);
  const currency = quoteData.currencyCode || '';

  const baseQuoteProduct = quoteData.products.find((p) => p.productId === baseProductId);
  if (!baseQuoteProduct) {
    return {
      region,
      currency,
      items: [],
      isDiscountedProduct: false
    };
  }

  const addonQuoteProducts = quoteData.products.filter(
    (p) => ADD_ON_SKUS.includes(p.productId) && addonIds?.includes(p.productId)
  );

  const baseItem = mapQuoteProductToOrderItem(baseQuoteProduct, currency);
  const subProducts = addonQuoteProducts.map((addon) =>
    mapQuoteProductToProductData(addon, region, currency));

  const allItems = [baseQuoteProduct, ...addonQuoteProducts];
  const totalPrice = allItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const totalDiscountAmt = allItems.reduce((sum, item) => sum + getDiscountAmount(item), 0);

  return {
    region,
    totalPrice,
    totalPriceWithoutTax: totalPrice,
    totalDiscountAmt,
    tax: 0,
    currency,
    items: [{
      ...baseItem,
      subProducts: subProducts.length > 0 ? subProducts : undefined
    }],
    isDiscountedProduct: totalDiscountAmt > 0
  };
};
